import * as yup from 'yup'
import { maxLength, minLength, requiredMessage, validMessage } from '../utils'
import { BillingType, OfficeType, PaymentMethod, PaymentType, UserType } from '../../generated'
import {
  INVALID_EMAIL,
  MISCELLANEOUS,
  MULTIPLE_SPACES_REGEX,
  PASSWORD_MUST_DIFFER,
  PASSWORD_REGEX,
  PASSWORD_VALIDATION_MESSAGE,
  DURATION_FORMAT_MESSAGE,
  TIME_FORMAT_HH_MM_REGEX,
  TIME_FORMAT_HH_REGEX,
  TITLE_REGEX,
  OTP_REGEX,
  OTP_REQUIRED_MESSAGE,
  OTP_DIGITS_MESSAGE
} from '../constants'
import { UserRole } from '../../generated'
import { JournalEntryLineItemFormValues } from '../interfaceTypes/forms'

const passwordValidationSchema = { password: yup.string().required(requiredMessage('Password')) }

const emailValidationSchema = {
  email: yup.string().email(INVALID_EMAIL).required(requiredMessage('Email'))
}

const emailNotRequiredValidationSchema = {
  email: yup.string().email(INVALID_EMAIL)
}

const passwordAndRepeatPasswordSchema = {
  password: yup.string().required(requiredMessage('Password')).matches(PASSWORD_REGEX, PASSWORD_VALIDATION_MESSAGE),
  repeatPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm your Password')
}

export const newPasswordAndRepeatPasswordSchema = yup.object({
  newPassword: yup.string().required(requiredMessage('Password')).matches(PASSWORD_REGEX, PASSWORD_VALIDATION_MESSAGE),
  repeatPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm your Password')
})

/**
 * The profile card, where the user proves the session is theirs before replacing the
 * password. The new one is checked against the current one here as well as on the
 * server, so an unchanged password is caught before a round trip.
 */
export const changePasswordValidationSchema = yup.object({
  currentPassword: yup.string().required(requiredMessage('Current password')),
  newPassword: yup
    .string()
    .required(requiredMessage('New password'))
    .matches(PASSWORD_REGEX, PASSWORD_VALIDATION_MESSAGE)
    .notOneOf([yup.ref('currentPassword')], PASSWORD_MUST_DIFFER),
  repeatPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm your Password')
})

export const loginValidationSchema = yup.object({
  ...emailValidationSchema,
  ...passwordValidationSchema
})

export const mfaOtpValidationSchema = yup.object({
  otp: yup
    .string()
    .required(OTP_REQUIRED_MESSAGE)
    .matches(OTP_REGEX, OTP_DIGITS_MESSAGE)
})

export const forgotPasswordValidationSchema = yup.object({ ...emailValidationSchema })

export const resetPasswordValidationSchema = yup.object({
  ...passwordAndRepeatPasswordSchema
})

const firstNameSchema = { firstName: yup.string().required(requiredMessage('First Name')) }

const lastNameSchema = { lastName: yup.string().required(requiredMessage('Last Name')) }

// For client/contact forms: businessName OR (firstName + lastName) must be provided
const clientNameValidationSchema = {
  firstName: yup.string().when('businessName', {
    is: (val: string | null | undefined) => !val,
    then: yup.string().required('First Name or Business Name is required.'),
    otherwise: yup.string().nullable().notRequired()
  }),
  lastName: yup.string().when('businessName', {
    is: (val: string | null | undefined) => !val,
    then: yup.string().required('Last Name or Business Name is required.'),
    otherwise: yup.string().nullable().notRequired()
  }),
  businessName: yup.string().nullable().notRequired()
}

export const phoneNumberExtensionValidationSchema = {
  phoneNumberExtension: yup
    .string()
    .nullable()
    .max(6, maxLength('Extension', 6))
    .matches(/^\d*$/, 'It should be a number')
}

const externalUserRoleValidationSchema = {
  externalUserRoleId: yup.string().required('Required').nullable()
}

const departmentIdsSchema = {
  departmentIds: yup
    .array()
    .of(yup.string().required('Each department must be a string'))
    .optional()
}

const initialsSchema = {
  initials: yup
    .string()
    .min(2, minLength('Initials', 2))
    .matches(/^[a-zA-Z0-9]+$/, validMessage('Initials'))
    .notRequired()
}

export const registerUserValidationSchema = yup.object({
  ...firstNameSchema,
  ...lastNameSchema,
  ...emailValidationSchema,
  ...passwordAndRepeatPasswordSchema,
  ...departmentIdsSchema,
  ...initialsSchema,
  roleType: yup.string(),
  hourlyRate: yup
    .number()
    .typeError('Hourly Rate must be a number')
    .required(requiredMessage('Hourly Rate'))
    .min(0, 'Hourly Rate must be greater than or equal to 0')
    .max(10000, maxLength('Hourly Rate', 10))
})

export const updateRoleSchema = yup.object({
  roles: yup.array().of(yup.mixed<UserRole>().oneOf(Object.values(UserRole))),
  delegateIds: yup.array().of(yup.string()),
  hourlyRate: yup
    .number()
    .typeError('Hourly Rate must be a number')
    .required(requiredMessage('Hourly Rate'))
    .min(0, 'Hourly Rate must be greater than or equal to 0')
    .max(10000, maxLength('Hourly Rate', 10))
})

export const updateDepartmentSchema = yup.object({
  ...departmentIdsSchema
})

export const updateUserValidationSchema = yup.object({
  ...firstNameSchema,
  ...lastNameSchema
})

export const updateNameSchema = yup.object({
  ...firstNameSchema,
  ...lastNameSchema
})

export const updateEmailSchema = yup.object({
  ...emailValidationSchema
})

export const updateInitialsSchema = yup.object({
  ...initialsSchema
})

const docketNumberValidationSchema = {
  docketNo: yup
    .string()
    .when('docketNo', val => {
      if (val?.length > 0) {
        return yup.string().max(50, maxLength('Docket Number', 50))
      } else {
        return yup.string().notRequired()
      }
    })
    .nullable()
    .optional()
}

const firstNameNotRequiredValidationSchema = {
  firstName: yup
    .string()
    .when('firstName', val => {
      if (val?.length > 0) {
        return yup
          .string()
          .min(2, minLength('First Name', 2))
          .max(100, maxLength('First Name', 100))
          .matches(MULTIPLE_SPACES_REGEX, validMessage('First Name'))
          .required(requiredMessage('First Name'))
      } else {
        return yup.string().notRequired()
      }
    })
    .nullable()
    .optional()
}

const lastNameNotRequiredValidationSchema = {
  lastName: yup
    .string()
    .when('lastName', val => {
      if (val?.length > 0) {
        return yup
          .string()
          .min(2, minLength('Last Name', 2))
          .max(100, maxLength('Last name', 100))
          .required(requiredMessage('Last Name'))
          .matches(MULTIPLE_SPACES_REGEX, validMessage('Last Name'))
      } else {
        return yup.string().notRequired()
      }
    })
    .nullable()
    .optional()
}

export const caseTitleValidationSchema = {
  title: yup
    .string()
    .min(2, minLength('Matter Title', 2))
    .max(100, maxLength('Matter Title', 100))
    .required(requiredMessage('Matter Title'))
}

const billingTypes = Object.values(BillingType)
const billingTypeValidationSchema = {
  // Billing type is required for normal matters but optional for consult matters (consult matters do
  // not carry a billing type). `isConsultMatter` is supplied as yup validation context by the form.
  billingType: yup
    .string()
    .oneOf(billingTypes, 'Invalid billing type')
    .nullable()
    .when('$isConsultMatter', {
      is: true,
      then: yup.string().oneOf(billingTypes, 'Invalid billing type').nullable().notRequired(),
      otherwise: yup
        .string()
        .oneOf(billingTypes, 'Invalid billing type')
        .required(requiredMessage('Billing Type'))
        .typeError(requiredMessage('Billing Type'))
    })
}

export const caseBillingValidationSchema = {
  contingencyShare: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .nullable()
    .label('Contingency Share')
    .when('billingType', {
      is: (val: BillingType) => val === BillingType.Contingency || val === BillingType.ContingencyHybrid,
      then: yup.number().required().min(0).max(100).typeError(requiredMessage('Contingency Share')),
      otherwise: yup
        .number()
        .transform((val, orig) => (orig === '' ? null : val))
        .min(0)
        .max(100)
        .nullable()
        .notRequired()
    }),
  contingencySettlementAmount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Settlement Amount')
    .min(0)
    .nullable()
    .notRequired(),
  flatFee: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .nullable()
    .label('Flat Fee')
    .when('billingType', {
      is: (val: BillingType) =>
        val === BillingType.FlatFee || val === BillingType.ContingencyHybrid || val === BillingType.Hybrid,
      then: yup.number().required().typeError(requiredMessage('Flat Fee')),
      otherwise: yup
        .number()
        .transform((val, orig) => (orig === '' ? null : val))
        .nullable()
        .notRequired()
    }),
  upfrontFlatFee: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Upfront Flat Fee')
    .when('billingType', {
      is: (val: BillingType) => val === BillingType.FlatFee,
      then: yup.number().required().typeError(requiredMessage('Upfront Flat Fee')),
      otherwise: yup.number().nullable().notRequired()
    }),
  monthlyFlatFee: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .notRequired()
    .nullable(),
  billingCycle: yup
    .string()
    .nullable()
    .when('billingType', {
      is: (val: BillingType) =>
        val === BillingType.FlatFee || val === BillingType.Hourly || val === BillingType.Hybrid,
      then: yup
        .string()
        .required(requiredMessage('Billing Cycle'))
        .typeError(requiredMessage('Billing Cycle')),
      otherwise: yup.string().notRequired().nullable()
    }),
  retainerAccountId: yup.string().when('billingType', {
    is: (val: BillingType) => val === BillingType.Hourly || val === BillingType.Contingency,
    then: yup.string().required('Required').typeError('Required'),
    otherwise: yup.string().notRequired().nullable()
  }),
  retainerAmount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Retainer Amount')
    .when('billingType', {
      is: (val: BillingType) => val === BillingType.Hourly,
      then: yup.number().required(requiredMessage('Retainer Amount')).typeError(requiredMessage('Retainer Amount')),
      otherwise: yup.number().notRequired().nullable()
    })
    .nullable(),
  billEntireFlatFeeUpfront: yup.boolean().nullable(),
  billGenerationLimitEnabled: yup.boolean().nullable(),
  billGenerationType: yup.string().label('Type').nullable(),
  billGenerationAmount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Amount')
    .nullable(),
  consultationFee: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Consultation Fee')
    .when('billingType', {
      is: (val: BillingType) => val === BillingType.Consult,
      then: yup.number().required(requiredMessage('Consultation Fee')).typeError(requiredMessage('Consultation Fee')),
      otherwise: yup.number().notRequired().nullable()
    }),
  billGenerationHours: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Hours')
    .nullable(),
  billApproverId: yup.string().label('Bill Approver').nullable(),
  writeOffInterest: yup.boolean().nullable(),
  interestRates: yup.array().of(
    yup.object({
      rate: yup
        .number()
        .transform((val, orig) => (orig === '' ? null : val))
        .label('Interest Rate')
        .min(0)
        .max(100)
        .nullable(),
      applicableDays: yup
        .number()
        .transform((val, orig) => (orig === '' ? null : val))
        .label('Applicable No. of Days')
        .nullable()
    })
  ),
  increaseInterestRateOnFirstLatePayment: yup.boolean().nullable(),
  discountType: yup.string().label('Type').nullable(),
  discountAmount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Amount')
    .nullable(),
  discountPercentage: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .label('Percentage')
    .min(0)
    .max(100)
    .nullable(),
  billingCycleCustomDate: yup.number().nullable().label('Custom Date').typeError('Invalid date'),
  minimumFundsThreshold: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .nullable()
    .label('Minimum Funds Threshold')
    .typeError('Invalid type'),
  pauseBillingUntil: yup.string().nullable().typeError(requiredMessage('Date')),
  paymentPlanStartDate: yup
    .string()
    .nullable()
    .typeError(requiredMessage('Date'))
    .when('billingType', {
      is: (val: BillingType) => val === BillingType.FlatFee,
      then: yup
        .string()
        .required(requiredMessage('Monthly Payments Start Date'))
        .typeError(requiredMessage('Monthly Payments Start Date')),
      otherwise: yup.string().notRequired().nullable()
    }),
  specialPayments: yup
    .array()
    .of(
      yup.object({
        amount: yup.number().required(requiredMessage('Amount')).typeError(requiredMessage('Amount')),
        date: yup.string().required(requiredMessage('Date')).typeError(requiredMessage('Date'))
      })
    )
    .nullable()
    .optional()
}

export const caseSettingValidationSchema = {
  notifyOnFlatFeeHoursThreshold: yup.boolean().nullable(),
  flatFeeHoursNotificationThreshold: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .nullable(),
  sendFlatFeeHoursExhaustedNotification: yup.boolean().nullable()
}

export const referencesValidationSchema = {
  sourceTypeId: yup.string(),
  referenceId: yup.string().when('sourceTypeId', {
    is: (val: string) => !!val,
    then: yup.string().required('Reference is required when type is selected'),
    otherwise: yup.string().notRequired().nullable()
  }),
  percentage: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .min(0, '% must be ≥ 0')
    .max(100, '% must be ≤ 100')
    .typeError('% must be a number')
}

export const createCaseValidationSchema = yup.object().shape(
  {
    ...caseTitleValidationSchema,
    description: yup.string(),
    lawType: yup.object().shape({
      id: yup.string().required(requiredMessage('Law Type')),
      label: yup.string()
    }),
    caseType: yup.object().shape({
      id: yup.string().required(requiredMessage('Matter Type')),
      label: yup.string()
    }),
    profitCenter: yup.object().shape({
      name: yup.string(),
      id: yup.number().required(requiredMessage('Profit Center'))
    }),
    location: yup.object().shape({
      name: yup.string(),
      id: yup.number().required(requiredMessage('Location'))
    }),
    caseNumber: yup.string().when('isConsultMatter', {
      is: (val: boolean) => val === true,
      then: yup.string().notRequired().nullable(),
      otherwise: yup
        .string()
        .min(4, minLength('Matter Number', 4))
        .max(20, maxLength('Matter Number', 20))
        .matches(/^\d+$/, 'It should be a number')
        .required('Matter Number is required')
        .nullable()
    }),
    consultMatterNumber: yup.string().when('isConsultMatter', {
      is: (val: boolean) => val === true,
      then: yup
        .string()
        .min(4, minLength('Consult Matter Number', 4))
        .max(15, maxLength('Consult Matter Number', 15))
        .matches(/\d+$/, 'Consult Matter Numbers must be numbers.')
        .required('Consult Matter Number is required')
        .nullable(),
      otherwise: yup.string().notRequired().nullable()
    }),
    openedBy: yup.string().when('isConsultMatter', {
      is: (val: boolean) => val === true,
      then: yup.string().required('Opened By is required').nullable(),
      otherwise: yup.string().notRequired().nullable()
    }),
    // Required when opening a consult, ignored for regular matters (where the field is not even rendered).
    consultAttorneyId: yup.string().when('isConsultMatter', {
      is: (val: boolean) => val === true,
      then: yup.string().required('Consult Attorney is required').nullable(),
      otherwise: yup.string().notRequired().nullable()
    }),
    judgeUser: yup
      .object()
      .shape({
        id: yup.string()
      })
      .nullable(),
    assignees: yup
      .array()
      .of(
        yup.object({
          id: yup.string().required(requiredMessage('Assignee')),
          label: yup.string()
        })
      )
      .min(1, 'Assignee is required'),
    ...docketNumberValidationSchema,
    judge: yup.object().shape(
      {
        ...firstNameNotRequiredValidationSchema,
        ...lastNameNotRequiredValidationSchema,
        ...emailNotRequiredValidationSchema
        // ...phoneNumberExtensionValidationSchema
      },
      [
        // Cyclic dependency for firstName and LastName
        ['firstName', 'firstName'],
        ['lastName', 'lastName']
      ]
    ),
    billing: yup.object().shape({
      ...billingTypeValidationSchema,
      ...caseBillingValidationSchema
    }),
    setting: yup.object().shape({
      ...caseSettingValidationSchema
    }),
    references: yup
      .array()
      .of(
        yup.object().shape({
          ...referencesValidationSchema
        })
      )
      .nullable(),
    client: yup
      .object({
        id: yup.string().required(requiredMessage('Primary Client')),
        caseRole: yup.string().nullable(),
        isBillParticipant: yup.boolean().nullable(),
        wantsBillDelivery: yup.boolean().nullable(),
        billDeliveryMethods: yup.array().of(yup.string()).nullable(),
        billingAddress: yup
          .string()
          .nullable()
          .when('billDeliveryMethods', {
            is: (methods: string[] | null | undefined) => !!methods?.includes('PAPER'),
            then: schema => schema.required(requiredMessage('Billing Address')),
            otherwise: schema => schema.nullable()
          }),
        splitBillShare: yup
          .number()
          .transform((val, orig) => (orig === '' ? null : val))
          .nullable()
          .min(0)
          .max(100)
          .label('Share')
      })
      .nullable()
      .required(requiredMessage('Primary Client')),
    clientUsers: yup
      .array()
      .of(
        yup
          .object({
            id: yup.string().nullable(),
            caseRole: yup
              .string()
              .nullable()
              .when('id', {
                is: (id: string | null | undefined) => !!id,
                then: schema => schema.required(requiredMessage('Matter Role')),
                otherwise: schema => schema.notRequired()
              }),
            isBillParticipant: yup.boolean().nullable(),
            wantsBillDelivery: yup.boolean().nullable(),
            billDeliveryMethods: yup.array().of(yup.string()).nullable(),
            billingAddress: yup
              .string()
              .nullable()
              .when('billDeliveryMethods', {
                is: (methods: string[] | null | undefined) => !!methods?.includes('PAPER'),
                then: schema => schema.required(requiredMessage('Billing Address')),
                otherwise: schema => schema.nullable()
              }),
            splitBillShare: yup
              .number()
              .transform((val, orig) => (orig === '' ? null : val))
              .nullable()
              .min(0)
              .max(100)
              .label('Share')
          })
          .nullable()
      )
      .test('unique-id-caseRole', 'Each contact must have a unique matter role.', clientUsers => {
        if (!clientUsers) return true

        const seen = new Set()
        for (const user of clientUsers) {
          if (!user) continue
          const key = `${user.id}::${user.caseRole}`
          if (seen.has(key)) {
            return false
          }
          user.id && seen.add(key)
        }
        return true
      }),
    adversaryUsers: yup
      .array()
      .of(
        yup
          .object({
            id: yup.string().nullable(),
            caseRole: yup
              .string()
              .nullable()
              .when('id', {
                is: (id: string | null | undefined) => !!id,
                then: schema => schema.required(requiredMessage('Matter Role')),
                otherwise: schema => schema.notRequired()
              })
          })
          .nullable()
      )
      .test('unique-id-caseRole', 'Each contact must have a unique matter role.', adversaryUsers => {
        if (!adversaryUsers) return true

        const seen = new Set()
        for (const user of adversaryUsers) {
          const key = `${user?.id}::${user?.caseRole}`
          if (seen.has(key)) {
            return false
          }
          user?.id && seen.add(key)
        }
        return true
      }),
    legalTeam: yup.array().of(
      yup.object({
        userType: yup.mixed<UserType>().required(),
        hourlyRate: yup.string().nullable(),
        userId: yup.string().when('userType', (userType, schema) => {
          if (userType === UserType.OriginatorAttorney) {
            return schema?.required('Originating Attorney is required')
          }
          if (userType === UserType.ResponsibleAttorney_1 || userType === UserType.Paralegal) {
            return schema?.required(
              userType === UserType.ResponsibleAttorney_1
                ? 'Responsible Attorney 1 is required'
                : 'Paralegal is required'
            )
          }
          return schema?.nullable()?.notRequired()
        })
      })
    ),
    ccEmailBillsTo: yup
      .string()
      .nullable()
      .test('valid-emails', 'Each entry must be a valid email address, seprated by ;', value => {
        if (!value) return true
        return value
          .split(';')
          .map(e => e.trim())
          .filter(Boolean)
          .every(email => yup.string().email().isValidSync(email))
      })
  },
  [
    // Cyclic dependency for docketNo
    ['docketNo', 'docketNo']
  ]
)

export const addTaskToUserValidationSchema = yup.object({
  assignee: yup.object().shape({
    id: yup.string().required(requiredMessage('Assignee')),
    label: yup.string(),
    role: yup.string()
  }),
  followUpDate: yup.string().nullable().required(requiredMessage('Follow Up Date'))
})

export const editAssignedTaskValidationSchema = yup.object({
  followUpDate: yup.string().nullable().required(requiredMessage('Follow Up Date'))
})

export const clientNumberValidationSchema = {
  clientNumber: yup
    .string()
    .min(4, minLength('Client Number', 4))
    .max(10, maxLength('Client Number', 10))
    .matches(/^\d+$/, 'It should be a number')
    .required('Client Number is required')
    .nullable()
}

export const addNewClientValidationSchema = yup.object({
  ...clientNameValidationSchema,
  ...externalUserRoleValidationSchema,
  ...clientNumberValidationSchema,
  initials: yup.string().max(4, maxLength('Initials', 4)).nullable().notRequired(),
  email: yup.string().email(INVALID_EMAIL).notRequired(),
  lastPayDate: yup.string().nullable(),
  socialSecurityNumber: yup.string().nullable().notRequired().max(9, maxLength('Social Security Number', 9)),
  driverLicenseNumber: yup.string().nullable().notRequired().max(20, maxLength('Driver License Number', 20)),
  addresses: yup.array().of(
    yup.object().shape({
      address1: yup.string().max(100, maxLength('Address line 1', 100)),
      address2: yup.string().max(100, maxLength('Address line 2', 100)),
      zipCode: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('Zip Code', 20))
        .matches(/^\d+(-\d+)?$/, 'It should be a number'),
      city: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('City', 20))
    })
  )
})

export const addNewExternalUserValidationSchema = yup.object({
  ...clientNameValidationSchema,
  ...externalUserRoleValidationSchema,
  ...emailNotRequiredValidationSchema,
  addresses: yup.array().of(
    yup.object().shape({
      address1: yup.string().max(100, maxLength('Address line 1', 100)),
      address2: yup.string().max(100, maxLength('Address line 2', 100)),
      zipCode: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('Zip Code', 20))
        .matches(/^\d+(-\d+)?$/, 'It should be a number'),
      city: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('City', 20))
    })
  )
})

export const addNewExternalUserAddressValidationSchema = yup.object({
  address1: yup.string().max(100, maxLength('Address line 1', 100)),
  address2: yup.string().max(100, maxLength('Address line 2', 100)),
  zipCode: yup
    .string()
    .nullable()
    .notRequired()
    .max(20, maxLength('Zip Code', 20))
    .matches(/^\d+$/, 'It should be a number'),
  city: yup
    .string()
    .nullable()
    .notRequired()
    .max(20, maxLength('City', 20))
})

export const addNewJudgeValidationSchema = yup.object({
  ...firstNameSchema,
  ...lastNameSchema,
  ...externalUserRoleValidationSchema,
  ...emailNotRequiredValidationSchema,
  ...phoneNumberExtensionValidationSchema
})

export const updateJudgeValidationSchema = yup.object({
  firstName: yup.string().when('businessName', {
    is: (val: string) => !val,
    then: schema => schema.required(requiredMessage('First Name')),
    otherwise: schema => schema.nullable().notRequired()
  }),
  lastName: yup.string().when('businessName', {
    is: (val: string) => !val,
    then: schema => schema.required(requiredMessage('Last Name')),
    otherwise: schema => schema.nullable().notRequired()
  }),
  businessName: yup.string().nullable().notRequired(),
  ...emailNotRequiredValidationSchema,
  ...phoneNumberExtensionValidationSchema
})

export const updateClientValidationSchema = yup.object({
  firstName: yup.string().when('businessName', {
    is: (val: string) => !val,
    then: schema => schema.required(requiredMessage('First Name')),
    otherwise: schema => schema.nullable().notRequired()
  }),
  lastName: yup.string().when('businessName', {
    is: (val: string) => !val,
    then: schema => schema.required(requiredMessage('Last Name')),
    otherwise: schema => schema.nullable().notRequired()
  }),
  businessName: yup.string().nullable().notRequired(),
  ...emailNotRequiredValidationSchema,
  ...clientNumberValidationSchema,
  contractBalance: yup.string().nullable().notRequired(),
  arrearsBalance: yup.string().nullable().notRequired(),
  lastPayDate: yup.string().nullable(),
  trustBalance: yup.string().nullable().notRequired(),
  residualBalance: yup.string().nullable().notRequired()
})

export const addCaseTaskValidationSchema = yup.object({
  title: yup
    .string()
    .min(2, minLength('Matter Task', 2))
    .max(100, maxLength('Matter task', 100))
    .matches(TITLE_REGEX, validMessage('Matter Task'))
    .required(requiredMessage('Matter Task'))
})

export const createCaseEventValidationSchema = yup.object({
  eventTitle: yup.object().shape({
    id: yup.string().required(requiredMessage('Event Title')),
    name: yup.string()
  }),
  hearingAt: yup.string().nullable().required(requiredMessage('Event Date')),
  hearingAtTime: yup.string().nullable().required(requiredMessage('Start Time')),
  hearingEndsAtTime: yup.string().nullable().required(requiredMessage('End Time')),
  title: yup.string().when('eventTitle', {
    is: (val: { id: string; name: string }) => !!(val && val.name === MISCELLANEOUS),
    then: yup
      .string()
      .min(2, minLength('Title', 2))
      .max(100, maxLength('Title', 100))
      .matches(TITLE_REGEX, validMessage('Title'))
      .required(requiredMessage('Title')),
    otherwise: yup.string().notRequired()
  })
})

export const addCaseNoteValidationSchema = yup.object({
  description: yup
    .string()
    .min(2, minLength('Note', 2))
    .required(requiredMessage('Note'))
    .max(10000, maxLength('Note', 10000))
})

export const updateCaseValidationSchema = yup.object().shape(
  {
    ...caseTitleValidationSchema,
    description: yup.string(),
    statusType: yup.object().shape({
      value: yup.string().required(requiredMessage('status')),
      label: yup.string()
    }),
    caseNumber: yup.string().when('isConsultMatter', {
      is: (val: boolean) => val === true,
      then: yup.string().notRequired().nullable(),
      otherwise: yup
        .string()
        .min(5, minLength('Matter Number', 5))
        .max(20, maxLength('Matter Number', 20))
        .matches(/^\d+$/, 'It should be a number')
        .required('Matter Number is required')
        .nullable()
    }),
    consultMatterNumber: yup.string().when('isConsultMatter', {
      is: (val: boolean) => val === true,
      then: yup
        .string()
        .min(5, minLength('Consult Matter Number', 5))
        .max(10, maxLength('Consult Matter Number', 10))
        .matches(/\d+$/, 'Consult Matter Numbers must be numbers.')
        .required('Consult Matter Number is required')
        .nullable(),
      otherwise: yup.string().notRequired().nullable()
    }),
    // Deliberately NOT required on update: consults that predate this field and converted consults awaiting
    // the client's list have no value yet, and requiring it here would block every other edit to them.
    consultAttorneyId: yup.string().notRequired().nullable(),
    lawType: yup.object().shape({
      id: yup.string().required(requiredMessage('Law Type')),
      label: yup.string()
    }),
    profitCenter: yup.object().shape({
      name: yup.string(),
      id: yup.number().required(requiredMessage('Profit Center'))
    }),
    location: yup.object().shape({
      name: yup.string(),
      id: yup.number().required(requiredMessage('Location'))
    }),
    caseType: yup.object().shape({
      id: yup.string().required(requiredMessage('Matter Type')),
      label: yup.string()
    }),
    ...docketNumberValidationSchema,
    judge: yup.object().shape(
      {
        ...firstNameNotRequiredValidationSchema,
        ...lastNameNotRequiredValidationSchema,
        ...emailNotRequiredValidationSchema
      },
      [
        // Cyclic dependency for firstName and LastName
        ['firstName', 'firstName'],
        ['lastName', 'lastName']
      ]
    ),
    billing: yup.object().shape({
      ...billingTypeValidationSchema,
      ...caseBillingValidationSchema
    }),
    setting: yup.object().shape({
      ...caseSettingValidationSchema
    }),
    client: yup
      .object({
        id: yup.string().required(requiredMessage('Primary Client')),
        caseRole: yup.string().nullable(),
        isBillParticipant: yup.boolean().nullable(),
        wantsBillDelivery: yup.boolean().nullable(),
        billDeliveryMethods: yup.array().of(yup.string()).nullable(),
        billingAddress: yup
          .string()
          .nullable()
          .when('billDeliveryMethods', {
            is: (methods: string[] | null | undefined) => !!methods?.includes('PAPER'),
            then: schema => schema.required(requiredMessage('Billing Address')),
            otherwise: schema => schema.nullable()
          }),
        splitBillShare: yup
          .number()
          .transform((val, orig) => (orig === '' ? null : val))
          .nullable()
          .min(0)
          .max(100)
          .label('Share')
      })
      .nullable()
      .required(requiredMessage('Primary Client')),
    clientUsers: yup.array().of(
      yup
        .object({
          id: yup.string().nullable(),
          caseRole: yup
            .string()
            .nullable()
            .when('id', {
              is: (id: string | null | undefined) => !!id,
              then: schema => schema.required(requiredMessage('Matter Role')),
              otherwise: schema => schema.notRequired()
            }),
          isBillParticipant: yup.boolean().nullable(),
          wantsBillDelivery: yup.boolean().nullable(),
          billDeliveryMethods: yup.array().of(yup.string()).nullable(),
          billingAddress: yup
            .string()
            .nullable()
            .when('billDeliveryMethods', {
              is: (methods: string[] | null | undefined) => !!methods?.includes('PAPER'),
              then: schema => schema.required(requiredMessage('Billing Address')),
              otherwise: schema => schema.nullable()
            }),
          splitBillShare: yup
            .number()
            .transform((val, orig) => (orig === '' ? null : val))
            .nullable()
            .min(0)
            .max(100)
            .label('Share')
        })
        .nullable()
    ),
    adversaryUsers: yup.array().of(
      yup
        .object({
          id: yup.string().nullable(),
          caseRole: yup
            .string()
            .nullable()
            .when('id', {
              is: (id: string | null | undefined) => !!id,
              then: schema => schema.required(requiredMessage('Matter Role')),
              otherwise: schema => schema.notRequired()
            })
        })
        .nullable()
    ),
    legalTeam: yup.array().of(
      yup.object().shape({
        userId: yup.string().when('userType', (userType: UserType, schema: yup.StringSchema) => {
          if (userType === UserType.OriginatorAttorney) {
            return schema.required('Originating Attorney is required')
          }
          if (userType === UserType.ResponsibleAttorney_1) {
            return schema.required('Responsible Attorney 1 is required')
          }
          if (userType === UserType.Paralegal) {
            return schema.required('Paralegal is required')
          }
          return schema.notRequired()
        }),
        userType: yup.mixed<UserType>().required(),
        hourlyRate: yup.string().nullable()
      })
    ),
    references: yup
      .array()
      .of(
        yup.object().shape({
          ...referencesValidationSchema
        })
      )
      .nullable(),
    ccEmailBillsTo: yup
      .string()
      .nullable()
      .test('valid-emails', 'Each entry must be a valid email address, seprated by ;', value => {
        if (!value) return true
        return value
          .split(';')
          .map(e => e.trim())
          .filter(Boolean)
          .every(email => yup.string().email().isValidSync(email))
      })
  },
  [
    // Cyclic dependency for docketNo
    ['docketNo', 'docketNo']
  ]
)

export const updateCaseEventValidationSchema = yup.object({
  hearingAt: yup.string().nullable().required(requiredMessage('Start Date')),
  hearingAtTime: yup.string().nullable().required(requiredMessage('Start Time')),
  hearingEndsAtTime: yup.string().nullable().required(requiredMessage('End Time')),
  eventTitle: yup.object().shape({
    id: yup.string().required(requiredMessage('Event Title')),
    name: yup.string()
  })
})

export const updateLawTypeValidationSchema = yup.object({
  lawType: yup.object().shape({
    id: yup.string().required(requiredMessage('Law Type')),
    name: yup.string()
  }),
  caseType: yup.object().shape({
    id: yup.string().required(requiredMessage('Matter Type')),
    name: yup.string()
  })
})

export const addJudgeValidationSchema = yup.object({
  judgeUser: yup
    .object()
    .shape({
      id: yup.string().required(requiredMessage('Judge')),
      firstName: yup.string()
    })
    .nullable()
})

export const addAdversaryValidationSchema = yup.object({
  adversaryUser: yup.object().shape({
    id: yup.string().required(requiredMessage('Adversary')),
    firstName: yup.string()
  })
})

export const createCourtValidationSchema = yup.object({
  name: yup
    .string()
    .min(3, minLength('Court Name', 3))
    .max(100, maxLength('Court Name', 100))
    .required(requiredMessage('Court Name')),
  county: yup.string().max(100, maxLength('County', 100))
})

const titleValidationSchema = {
  name: yup
    .string()
    .min(3, minLength('Title', 3))
    .max(100, maxLength('Title', 100))
    .matches(/^[A-Za-z\s/&]*$/, 'Please add a valid title')
    .required(requiredMessage('Title'))
}

export const createLawTypeValidationSchema = yup.object({
  ...titleValidationSchema
})

export const createCaseTypeValidationSchema = yup.object({
  ...titleValidationSchema
})

export const createCustomTaskValidationSchema = yup.object({
  title: yup.string().min(3, minLength('Title', 3)).max(100, maxLength('Title', 100)).required(requiredMessage('Title'))
})

// Minutes from either accepted duration shape, without going through
// `durationToMinutes` (utils imports from here, so keep this self-contained).
// "1:30" -> 90, "1.5" -> 90, "00:00" -> 0.
const durationInMinutes = (value: string) => {
  const [hours = '0', minutes = '0', seconds = '0'] = value.split(':')
  return Number(hours) * 60 + Number(minutes) + Number(seconds) / 60
}

/**
 * A duration is either HH:MM(:SS) or decimal hours ("1.5"). Empty passes — each
 * form decides on its own whether the field is required.
 *
 * Both problems are *reported* rather than silently corrected: `handleDuration`
 * used to blank the field on blur, which threw the typed value away before any
 * message could be shown, so a typo just looked like the field had emptied
 * itself.
 */
const durationField = () =>
  yup
    .string()
    .test(
      'is-valid-duration',
      DURATION_FORMAT_MESSAGE,
      value => !value || TIME_FORMAT_HH_MM_REGEX.test(value) || TIME_FORMAT_HH_REGEX.test(value)
    )
    .test('is-non-zero-duration', 'Duration must be greater than 00:00', value => {
      if (!value) return true
      // Let the format test above own the message for unparseable input.
      if (!TIME_FORMAT_HH_MM_REGEX.test(value) && !TIME_FORMAT_HH_REGEX.test(value)) return true
      return durationInMinutes(value) > 0
    })

export const timerSchema = yup.object().shape({
  // The timer form had no duration rule at all, so a typo here could never
  // surface an error — it was only ever wiped by the blur handler.
  duration: durationField(),
  caseId: yup.string().nullable().required('Matter is required'),
  // Unconditionally required. The old rule keyed off `'isAdmin'`/`'isParalegal'`
  // without the `$` prefix, so yup read them as *sibling fields* rather than
  // context — the form has no such fields, so the condition was always false
  // and the user could be cleared and submitted. An entry has to belong to
  // someone in every case, and the matter list is scoped by it.
  userId: yup.string().nullable().required('User is required'),
  description: yup.string(),
  billed: yup.boolean(),
  entryType: yup.string().required('Entry type is required'),
  rate: yup.number().when(['$isExpenseEntry', '$isLegalFee'], {
    is: (isExpenseEntry: boolean, isLegalFee: boolean) => !isExpenseEntry && !isLegalFee,
    then: yup.number().typeError('Rate must be a number').nullable().notRequired(),
    otherwise: yup.number().typeError('Rate must be a number').required('Rate is required')
  })
})

export const manualEntrySchema = yup.object().shape({
  // Was missing entirely, so the User dropdown could be cleared and saved.
  userId: yup.string().nullable().required('User is required'),
  caseId: yup
    .string()
    .nullable()
    .when('$isNonClientEntry', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Matter is required')
    }),
  duration: yup.string().when(['$isExpenseEntry', '$isLegalFee'], {
    is: (isExpenseEntry: boolean, isLegalFee: boolean) => !isExpenseEntry && !isLegalFee,
    then: durationField(),
    otherwise: yup.string().notRequired()
  }),
  description: yup.string(),
  date: yup.string().required('Date is required').typeError(requiredMessage('Date')),
  billed: yup.boolean(),
  rate: yup.number().when(['$isExpenseEntry', '$isLegalFee'], {
    is: (isExpenseEntry: boolean, isLegalFee: boolean) => !isExpenseEntry && !isLegalFee,
    then: yup.number().typeError('Rate must be a number').nullable().notRequired(),
    otherwise: yup.number().typeError('Rate must be a number').required('Rate is required')
  }),

  categoryId: yup.string().when('$isExpenseEntry', {
    is: true,
    then: yup.string().required('Category is required'),
    otherwise: yup.string().notRequired()
  }),
  feeTypeId: yup.string().when('$isLegalFee', {
    is: true,
    then: yup.string().required('Fee Type is required').typeError('Fee Type is required'),
    otherwise: yup.string().notRequired().typeError('Fee Type is required')
  }),
  amount: yup.number().when('$isExpenseEntry', {
    is: true,
    then: yup.number().typeError('Please enter a valid amount').required('Amount is required'),
    otherwise: yup.number().typeError('Please enter a valid amount').notRequired()
  }),
  qty: yup.number().when('$isExpenseEntry', {
    is: true,
    then: yup
      .number()
      .typeError('Qty must be a number')
      .nullable()
      .transform((v, o) => (o === '' ? null : v))
      .notRequired(),
    otherwise: yup.number().notRequired()
  }),
  unitPrice: yup.number().when('$isExpenseEntry', {
    is: true,
    then: yup
      .number()
      .typeError('Unit price must be a number')
      .nullable()
      .transform((v, o) => (o === '' ? null : v))
      .notRequired(),
    otherwise: yup.number().notRequired()
  })
})

export const ConflictCheckSchema = yup.object({
  formError: yup.string().notRequired(),
  firstName: yup.string().notRequired(),
  lastName: yup.string().notRequired(),
  ...addNewExternalUserValidationSchema.omit(['externalUserRoleId', 'firstName', 'lastName']).fields,
  searchStyle: yup.string().required('Search Style is required'),
  pagination: yup.object().shape({
    page: yup.number().required(),
    limit: yup.number().required()
  })
})

export const addNewAccountValidationSchema = yup.object().shape({
  type: yup.string().required('Account type is required'),
  category: yup.string().required('Account category is required'),
  name: yup.string().min(3, minLength('Account name', 3)).required('Account name is required'),
  openingBalance: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .optional()
    .label('Opening Balance')
    .nullable(),
  sortCode: yup.string().required('Sort code is required'),
  routingNo: yup.string().label('Routing number').nullable().notRequired()
})

export const addCategoryValidationSchema = yup.object().shape({
  name: yup.string().min(3, minLength('Name', 3)).required(requiredMessage('Name')).trim(),
  description: yup.string().notRequired()
})

export const addFeeTypeValidationSchema = yup.object().shape({
  feeType: yup.string().min(3, minLength('Fee Type', 3)).required(requiredMessage('Fee Type')).trim()
})

export const editBillEntryValidationSchema = yup.object().shape({
  discountPercentage: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .typeError('Percentage must be a number')
    .positive('Percentage must be a positive number')
    .min(0, 'Percentage must be greater than or equal to 0')
    .max(100, 'Percentage must be less than or equal to 100')
    .nullable()
    .notRequired(),
  timeEntries: yup.array().of(
    yup.object().shape({
      date: yup.string().required('Date is required').typeError('Date is required'),
      userId: yup.string().required('User is required'),
      duration: yup
        .string()
        .required('Duration is required')
        .matches(TIME_FORMAT_HH_MM_REGEX, {
          message: DURATION_FORMAT_MESSAGE,
          excludeEmptyString: true
        })
        .typeError('Duration is required'),
      rate: yup
        .number()
        .typeError('Rate must be a number')
        .required('Rate is required')
        .transform((val, orig) => (orig === '' ? null : val))
        .min(0, 'Rate must be greater than or equal to 0'),
      description: yup.string().nullable().notRequired(),
      billable: yup.boolean(),
      showOnBill: yup.boolean(),
      sendNotesToMatterNotes: yup.boolean().optional()
    })
  ),
  legalFeeEntries: yup.array().of(
    yup.object().shape({
      date: yup.string().required('Date is required').typeError('Date is required'),
      userId: yup.string().when('isSystemGenerated', {
        is: (isSystemGenerated: boolean) => !!isSystemGenerated,
        then: schema => schema.notRequired(),
        otherwise: schema => schema.required('User is required')
      }),
      feeTypeId: yup.string().required('Fee Type is required').typeError('Fee Type is required'),
      amount: yup
        .number()
        .typeError('Fee must be a number')
        .required('Fee is required')
        .transform((val, orig) => (orig === '' ? null : val))
        .min(0, 'Fee must be greater than or equal to 0'),
      description: yup.string().nullable().notRequired(),
      billable: yup.boolean(),
      showOnBill: yup.boolean(),
      sendNotesToMatterNotes: yup.boolean().optional()
    })
  ),
  expenseEntries: yup.array().of(
    yup.object().shape({
      date: yup.string().required('Date is required').typeError('Date is required'),
      userId: yup.string().required('User is required'),
      categoryId: yup.string().required('Category is required').typeError('Category is required'),
      qty: yup
        .number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .notRequired(),
      unitPrice: yup
        .number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .notRequired(),
      amount: yup
        .number()
        .typeError('Amount must be a number')
        .required('Amount is required')
        .transform((val, orig) => (orig === '' ? null : val))
        .min(0, 'Amount must be greater than or equal to 0'),
      description: yup.string().nullable().notRequired(),
      billable: yup.boolean(),
      showOnBill: yup.boolean(),
      sendNotesToMatterNotes: yup.boolean().optional()
    })
  ),
  investigatorEntries: yup.array().of(
    yup.object().shape({
      date: yup.string().required('Date is required').typeError('Date is required'),
      userId: yup.string().required('User is required'),
      duration: yup
        .string()
        .required('Duration is required')
        .matches(TIME_FORMAT_HH_MM_REGEX, {
          message: DURATION_FORMAT_MESSAGE,
          excludeEmptyString: true
        })
        .typeError('Duration is required'),
      rate: yup
        .number()
        .transform((val, orig) => (orig === '' ? null : val))
        .typeError('Rate must be a number')
        .required('Rate is required')
        .min(0, 'Rate must be greater than or equal to 0'),
      description: yup.string().nullable().notRequired(),
      billable: yup.boolean(),
      showOnBill: yup.boolean(),
      sendNotesToMatterNotes: yup.boolean().optional()
    })
  )
})

export const referralSourceSchema = yup.object().shape({
  sourceType: yup.string().min(2, minLength('Source Type', 2)).required(requiredMessage('Source Type')).trim()
})

export const addDepartmentSchema = yup.object().shape({
  name: yup.string().min(2, minLength('Department Name', 2)).required(requiredMessage('Name')).trim(),
  departmentHeadId: yup.string().optional(),
  lawTypeIds: yup
    .array()
    .of(yup.string().required(requiredMessage('Law Type')))
    .optional()
})

export const cardPaymentSchema = yup.object().shape({
  cardNumber: yup
    .string()
    .matches(/^\d{13,16}$/, { message: 'Card number must be 13-16 digits', excludeEmptyString: true })
    .required('Card number is required'),
  expirationDate: yup
    .string()
    .matches(/^(0[1-9]|1[0-2])\/[0-9]{2}$/, {
      message: 'Expiration date must be in the format MM/YY',
      excludeEmptyString: true
    })
    .required('Expiration date is required'),
  cvv: yup
    .string()
    .matches(/^\d{3,4}$/, { message: 'CVV must be 3-4 digits', excludeEmptyString: true })
    .required('CVV is required'),
  cardHolderName: yup.string().min(3, minLength('Name', 3)).required('Card holder name is required'),
  amount: yup.number().min(1, 'Amount must be greater than zero').required('Amount is required')
})

export const bankPaymentSchema = yup.object({
  accountNumber: yup
    .string()
    .required('Account number is required')
    .matches(/^\d+$/, 'Account number must be numeric')
    .min(4, 'Account number is too short')
    .max(17, 'Account number is too long'),
  routingNumber: yup
    .string()
    .required('Routing number is required')
    .matches(/^\d{9}$/, 'Routing number must be exactly 9 digits'),
  nameOnAccount: yup.string().required('Name on account is required').min(2, 'Name is too short'),
  accountType: yup
    .mixed<'checking' | 'savings' | 'businessChecking'>()
    .oneOf(['checking', 'savings', 'businessChecking'], 'Invalid account type')
    .required('Account type is required'),
  partialAmount: yup.number().when('partialPayment', {
    is: true,
    then: yup.number().min(1, 'Partial amount must be greater than zero').required('Partial amount is required'),
    otherwise: yup.number().notRequired()
  })
})

export const referenceSchema = yup.object().shape({
  sourceTypeId: yup.string().min(2, minLength('Source Type', 2)).required(requiredMessage('Source Type')).trim(),
  reference: yup.string().min(2, minLength('Reference', 2)).required(requiredMessage('Reference')).trim()
})

export const refundTransactionSchema = yup.object({
  refundAmount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .min(0.01, 'Refund amount must be greater than zero')
    .typeError('Refund amount is required')
    .required('Refund amount is required'),
  description: yup.string().nullable().optional(),
  paymentMethod: yup.string().required('Payment method is required').typeError('Payment method is required'),
  referenceNo: yup.string().required('Reference number is required').typeError('Reference number is required'),
  slipNumber: yup.string().nullable().optional(),
  date: yup.string().required('Date is required').typeError('Date is required'),
  location: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number().required('Location is required')
    })
    .required('Location is required')
    .typeError('Location is required')
})

export const fundRequestValidationSchema = yup.object({
  case: yup
    .object()
    .shape({
      id: yup.string().required('Matter is required').typeError('Matter is required')
    })
    .typeError('Matter is required')
    .required('Matter is required'),
  amount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .positive('Amount must be positive')
    .required('Amount is required')
    .typeError('Amount is required'),
  description: yup.string().optional().nullable()
})

/** Create Fund Request confirmation modal (Trust Balance widget) */
export const createFundRequestConfirmSchema = yup.object({
  amount: yup
    .number()
    .transform((val, orig) => (orig === '' ? undefined : val))
    .required('Amount is required')
    .typeError('Amount is required')
    .positive('Amount must be greater than 0'),
  description: yup.string().optional().nullable()
})

export const recordPaymentValidationSchema = yup.object({
  amount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .positive('Amount must be greater than 0')
    .required('Amount is required')
    .typeError('Amount is required'),
  isAdvancePayment: yup.boolean().nullable(),
  pauseBillingUntil: yup
    .string()
    .nullable()
    .when('isAdvancePayment', {
      is: true,
      then: yup.string().required('Required').typeError('Required'),
      otherwise: yup.string().nullable()
    }),
  date: yup.string().required('Date is required').typeError('Date is required'),
  paymentMethod: yup
    .string()
    .oneOf(Object.values(PaymentMethod), 'Invalid payment method')
    .required('Payment method is required.'),
  referenceNo: yup.string().typeError('Invalid reference number').nullable().optional(),
  description: yup.string().optional().nullable(),
  destinationAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('Account is Required')
    })
    .required('Account is required')
    .typeError('Account is required'),
  location: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number()
    })
    .nullable()
    .optional(),
  client: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string()
    })
    .optional()
    .nullable(),
  vendor: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number()
    })
    .nullable()
    .optional(),
  glAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string()
    })
    .nullable()
    .optional()
})

export const createVendorSchema = yup.object().shape({
  name: yup.string().min(2, minLength('Vendor Name', 2)).required(requiredMessage('Vendor Name')).trim(),
  vendorId: yup.string().min(1, minLength('Vendor ID', 1)).required(requiredMessage('Vendor ID')).trim(),
  phone: yup.string().nullable().optional().trim(),
  separateCheckPerInvoice: yup.boolean().optional(),
  parentVendor: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number()
    })
    .nullable()
    .optional(),
  addresses: yup.array().of(
    yup.object().shape({
      address1: yup.string().max(100, maxLength('Address line 1', 100)).nullable().optional().trim(),
      address2: yup.string().max(100, maxLength('Address line 2', 100)).nullable().optional().trim(),
      zipCode: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('Zip Code', 20))
        .matches(/^\d+(-\d+)?$/, 'It should be a number'),
      city: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('City', 20))
        .matches(/^[A-Za-z0-9\s]*$/, 'It should be a string or number')
    })
  )
})

export const createOfficeSchema = yup.object().shape({
  name: yup.string().min(2, minLength('Profit Center Name', 2)).required(requiredMessage('Name')).trim(),
  email: yup.string().nullable().email(INVALID_EMAIL).trim(),
  phone: yup.string().nullable().min(10, minLength('Phone', 10)).max(10, maxLength('Phone', 10)).trim(),
  addresses: yup.array().of(
    yup.object().shape({
      address1: yup.string().nullable().max(100, maxLength('Address line 1', 100)),
      address2: yup.string().nullable().max(100, maxLength('Address line 2', 100)),
      zipCode: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('Zip Code', 20))
        .matches(/^\d+(-\d+)?$/, 'It should be a number'),
      city: yup
        .string()
        .nullable()
        .notRequired()
        .max(20, maxLength('City', 20))
        .matches(/^[A-Za-z\s]*$/, 'It should be a string')
    })
  ),
  type: yup.string().oneOf(Object.values(OfficeType), 'Invalid office type').required('Office type is required.')
})

const journalEntryLineItemSchema = yup.object().shape({
  account: yup
    .object()
    .shape({
      label: yup.string().required('GL Account is required'),
      value: yup.string().required('GL Account is required')
    })
    .required('GL Account is required')
    .typeError('GL Account is required'),
  office: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number()
    })
    .optional()
    .nullable()
    .typeError('Invalid value'),
  debitAmount: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .min(0, 'Amount must be positive')
    .test(
      'debit-or-credit',
      'Either Debit or Credit must have a value',
      function (value: JournalEntryLineItemFormValues['debitAmount'] | undefined | null) {
        const { creditAmount } = this.parent
        const hasDebit = value !== undefined && value !== null && value !== ''
        const hasCredit = creditAmount !== undefined && creditAmount !== null && creditAmount !== ''

        if (hasDebit || hasCredit) return true

        return false
      }
    )
    .nullable(),
  creditAmount: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .min(0, 'Amount must be positive')
    .test(
      'debit-or-credit',
      'Either Debit or Credit must have a value',
      function (value: JournalEntryLineItemFormValues['creditAmount'] | undefined | null) {
        const { debitAmount } = this.parent
        const hasDebit = debitAmount !== undefined && debitAmount !== null && debitAmount !== ''
        const hasCredit = value !== undefined && value !== null && value !== ''

        if (hasDebit || hasCredit) return true

        return false
      }
    )
    .nullable()
})

export const createManualJournalEntrySchema = yup.object().shape({
  date: yup.string().required('Date is required').typeError('Date is required'),
  description: yup.string().nullable().optional(),
  lineItems: yup
    .array()
    .of(journalEntryLineItemSchema)
    .min(2, 'At least 2 line items are required')
    .required('Line items are required')
    .typeError('Line items are required')
})

export const recordVendorPaymentSchema = yup.object().shape({
  paymentMethod: yup
    .string()
    .oneOf(Object.values(PaymentMethod), 'Invalid payment method')
    .required('Payment method is required.'),
  paymentType: yup
    .string()
    .oneOf(Object.values(PaymentType), 'Invalid payment type')
    .required('Payment type is required.'),
  amount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .positive('Amount must be greater than 0')
    .required('Amount is required')
    .typeError('Amount is required'),
  date: yup.string().required('Date is required').typeError('Date is required'),
  referenceNo: yup
    .string()
    .when('paymentMethod', val => {
      if (val !== PaymentMethod.Ach) {
        return yup.string().typeError('Reference number is required').required('Reference number is required')
      } else {
        return yup.string().notRequired()
      }
    })
    .required('Reference number is required')
    .typeError('Reference number is required'),
  office: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number()
    })
    .required('Office is required'),
  account: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('Account is Required')
    })
    .required('Account is required'),
  vendor: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number()
    })
    .notRequired()
    .nullable(),
  glAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('GL Account is Required')
    })
    .required('GL Account is required')
    .typeError('GL Account is required'),
  notes: yup.string().optional().nullable()
})

export const createAccountPayableSchema = yup.object().shape({
  isPaid: yup.boolean(),
  paymentMethod: yup
    .string()
    .transform(val => (typeof val === 'string' ? val.toLowerCase() : val))
    .oneOf(
      Object.values(PaymentMethod).map(v => v.toLowerCase()),
      'Invalid payment method'
    )
    .required('Payment method is required.'),
  amount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .positive('Amount must be greater than 0')
    .required('Amount is required')
    .typeError('Amount is required'),
  payDate: yup.string().required('Pay Date is required').typeError('Pay Date is required'),
  invoiceDate: yup.string().required('Invoice Date is required').typeError('Invoice Date is required'),
  office: yup.object().when('multipleLineItems', {
    is: true,
    then: yup.object().nullable().optional(),
    otherwise: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.number().required('Office is required')
      })
      .required('Office is required')
      .typeError('Office is required')
  }),
  bankAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('Bank Account is Required')
    })
    .required('Bank Account is required')
    .typeError('Bank Account is required'),
  vendor: yup
    .object({
      label: yup.string().required('Vendor is required'),
      value: yup.number().required('Vendor is required')
    })
    .nullable()
    .required('Vendor is required')
    .typeError('Vendor is required')
    .test(
      'vendor-required',
      'Vendor is required',
      value => value !== null && typeof value === 'object' && !!value.value
    ),
  matter: yup.string().nullable().typeError('Invalid Matter selected'),
  glAccount: yup.object().when('multipleLineItems', {
    is: true,
    then: yup.object().nullable().optional(),
    otherwise: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.string().required('GL Account is Required')
      })
      .required('GL Account is required')
      .typeError('GL Account is required')
  }),
  description: yup.string().optional().nullable(),
  multipleLineItems: yup.boolean().optional().nullable(),
  lineItems: yup.array().when('multipleLineItems', {
    is: true,
    then: yup.array().of(
      yup.object().shape({
        id: yup.string().nullable().optional(),
        glAccount: yup
          .object()
          .shape({
            label: yup.string(),
            value: yup.string().required('GL Account is Required')
          })
          .required('GL Account is required')
          .typeError('GL Account is required'),
        office: yup
          .object()
          .shape({
            label: yup.string(),
            value: yup.number().required('Office is required')
          })
          .required('Office is required')
          .typeError('Office is required'),
        matter: yup.string().nullable().typeError('Invalid value').optional(),
        client: yup
          .object()
          .shape({
            label: yup.string(),
            value: yup.string()
          })
          .nullable()
          .optional(),
        amount: yup
          .number()
          .transform((val, orig) => {
            if (orig === '') return null
            if (typeof val !== 'number' || Number.isNaN(val)) return val
            return Math.round(val * 100) / 100
          })
          // $0 is allowed only when editing a paid payable (context set by AddAccountPayableModal), where
          // zeroing is one way to empty a line whose ledger rows can't simply be recreated.
          .test('non-zero', 'Amount cannot be zero', function (val) {
            return val == null || val !== 0 || !!this.options.context?.allowZeroLineAmount
          })
          .required('Amount is required')
          .typeError('Amount is required')
      })
    ),
    otherwise: yup.array().nullable()
  })
})

export const createPayableRefundSchema = yup.object().shape({
  payable: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('Voucher is Required')
    })
    .required('Voucher is required')
    .typeError('Voucher is required'),
  paymentMethod: yup
    .string()
    .oneOf(Object.values(PaymentMethod), 'Invalid payment method')
    .required('Payment method is required.'),
  amount: yup
    .number()
    .transform((val, orig) => (orig === '' ? null : val))
    .positive('Amount must be greater than 0')
    .required('Amount is required')
    .typeError('Amount is required'),
  date: yup.string().required('Date is required').typeError('Date is required'),
  office: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number().required('Profit Center is Required')
    })
    .required('Profit Center is required')
    .typeError('Profit Center is required'),
  bankAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('Bank Account is Required')
    })
    .required('Bank Account is required')
    .typeError('Bank Account is required'),
  glAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('GL Account is Required')
    })
    .required('GL Account is required')
    .typeError('GL Account is required'),
  referenceNo: yup.string().nullable().optional(),
  description: yup.string().optional().nullable()
})

export const contingencyFinalBillValidationSchema = yup.object({
  calculationMethod: yup.string().oneOf(['percentage', 'fixed']).required('Calculation method is required'),
  settlementAmount: yup
    .number()
    .label('Settlement Amount')
    .transform((val, orig) => (orig === '' ? null : val))
    .positive('Settlement Amount must be greater than 0')
    .required('Settlement Amount is required')
    .typeError('Settlement Amount is required'),
  contingencyShare: yup
    .number()
    .label('Contingency Share')
    .transform((val, orig) => (orig === '' ? null : val))
    .when('calculationMethod', {
      is: 'percentage',
      then: schema =>
        schema
          .positive('Contingency Share must be greater than 0')
          .required('Contingency Share is required')
          .typeError('Contingency Share is required')
          .min(0)
          .max(100),
      otherwise: schema => schema.nullable().optional()
    }),
  fixedLegalFee: yup
    .number()
    .label('Legal Fee')
    .transform((val, orig) => (orig === '' ? null : val))
    .when('calculationMethod', {
      is: 'fixed',
      then: schema =>
        schema
          .positive('Legal Fee must be greater than 0')
          .required('Legal Fee is required')
          .typeError('Legal Fee is required'),
      otherwise: schema => schema.nullable().optional()
    })
})

export const contingencyFundsValidationSchema = yup.object({
  amount: yup
    .number()
    .label('Amount')
    .transform((val, orig) => (orig === '' ? null : val))
    .positive('Amount must be greater than 0')
    .required('Amount is required')
    .typeError('Amount is required'),
  trustAccountId: yup.string().required('Account is required').typeError('Account is required'),
  description: yup.string().optional().nullable()
})

export const settlementCheckValidationSchema = yup.object({
  checkNo: yup
    .string()
    .required(requiredMessage('Starting Check Number'))
    .typeError(requiredMessage('Starting Check Number')),
  checkDate: yup.string().required(requiredMessage('Check Date')).typeError(requiredMessage('Check Date')),
  memo: yup.string().optional().nullable()
})

export const transferFundsBetweenAccountsValidationSchema = yup.object({
  sourceAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('Source account is required')
    })
    .required('Source account is required')
    .typeError('Source account is required'),
  destinationAccount: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string().required('Destination account is required')
    })
    .required('Destination account is required')
    .typeError('Destination account is required'),
  amount: yup
    .number()
    .required('Amount is required')
    .positive('Amount must be greater than 0')
    .typeError('Amount is required'),
  date: yup.string().required('Date is required').typeError('Date is required'),
  paymentMethod: yup
    .string()
    .required('Payment method is required')
    .oneOf(Object.values(PaymentMethod), 'Invalid payment method')
    .typeError('Payment method is required'),
  referenceNumber: yup.string().optional().nullable(),
  clientId: yup.string().label('Client').optional().nullable(),
  caseId: yup.string().label('Matter').optional().nullable(),
  notes: yup.string().optional().nullable()
})

export const writeOffBillsValidationSchema = yup.object({
  bills: yup
    .array()
    .of(
      yup.object().shape({
        billId: yup.number().required('Invoice ID is required'),
        writtenOffPrincipal: yup
          .number()
          .transform((val, orig) => (orig === '' ? 0 : val))
          .when('isSelected', {
            is: true,
            then: schema => schema.max(yup.ref('principalBalance'), 'Exceeds invoice balance')
          })
          .nullable()
          .optional(),
        writtenOffInterest: yup
          .number()
          .transform((val, orig) => (orig === '' ? 0 : val))
          .when('isSelected', {
            is: true,
            then: schema => schema.max(yup.ref('interestDue'), 'Exceeds interest due')
          })
          .nullable()
          .optional(),
        isSelected: yup.boolean().optional().nullable()
      })
    )
    .required('At least one bill is required'),
  writtenOffReason: yup.string().nullable().optional()
})
